import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  iconBgColor?: string;
  valueColor?: string;
  loading?: boolean;
  badge?: React.ReactNode;
  prefix?: React.ReactNode;
}

/**
 * Shared statistics card component
 * Used in: KBDocumentsPage, KBEntityTypesPage, KBValidationPage
 */
const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  iconBgColor = 'bg-blue-100',
  valueColor,
  loading = false,
  badge,
  prefix,
}) => {
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-4">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mt-1" />
          ) : (
            <div className="flex items-center gap-2">
              {prefix}
              <p
                className="text-2xl font-bold"
                style={valueColor ? { color: valueColor } : undefined}
              >
                {value}
              </p>
              {badge}
            </div>
          )}
        </div>
        {icon && (
          <div className={`p-3 rounded-full ${iconBgColor}`}>
            {icon}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface StatsCardGridProps {
  children: React.ReactNode;
  cols?: 2 | 3 | 4;
}

/**
 * Grid container for StatCard components
 */
const StatsCardGrid: React.FC<StatsCardGridProps> = ({ children, cols = 3 }) => {
  const colsClass = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  }[cols];

  return (
    <div className={`grid grid-cols-1 ${colsClass} gap-4`}>
      {children}
    </div>
  );
};

/**
 * Alternative StatCard with header layout (used in EntityTypes)
 */
interface StatCardWithHeaderProps {
  title: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  valueColor?: string;
  valueColorClass?: string;
  loading?: boolean;
}

const StatCardWithHeader: React.FC<StatCardWithHeaderProps> = ({
  title,
  value,
  icon,
  valueColor,
  valueColorClass,
  loading = false,
}) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div
            className={`flex items-center gap-2 text-2xl font-bold ${valueColorClass || ''}`}
            style={valueColor ? { color: valueColor } : undefined}
          >
            {icon}
            {value}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export { StatCard, StatsCardGrid, StatCardWithHeader };
export default StatCard;
